import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import {
  getIdToken,
  parseActionCodeURL,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, redirect } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { toast } from 'sonner';

import { Toaster } from '@workspace/shadcn';
import { client, createMutationErrorHandler } from '@workspace/ui';

import { auth, getIdentity } from './app/firebase';
import Layout, { layoutLoader } from './app/layout.tsx';
import { enrollDestination } from './app/lib/enroll.ts';
import Checkout, { CheckoutError } from './app/routes/checkout.tsx';
import CourseDetail from './app/routes/course-detail.tsx';
import ForgotPassword from './app/routes/forgot-password/forgot-password.tsx';
import ResetPasswordEmailSent from './app/routes/forgot-password/reset-password-email-sent.tsx';
import MyCourses from './app/routes/home.tsx';
import ProfilePage from './app/routes/profile.tsx';
import PasswordReset from './app/routes/reset-password/password-reset.tsx';
import ResetPassword, {
  ResetPasswordError,
} from './app/routes/reset-password/reset-password.tsx';
import SignIn from './app/routes/signin.tsx';
import SignUp from './app/routes/signup.tsx';
import i18n from './i18n/i18n';

/**
 * Guest pages bounce an already-signed-in learner onwards. www links straight
 * to /signup?course=<slug> because it cannot see the session, so the redirect
 * has to carry that intent through to checkout rather than dropping it.
 */
async function guestOnly({ request }: { request: Request }) {
  const identity = await getIdentity();
  if (!identity) {
    return null;
  }
  const course = new URL(request.url).searchParams.get('course');
  throw redirect(enrollDestination(course));
}

const router = createBrowserRouter([
  {
    Component: Layout,
    loader: layoutLoader,
    shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate }) =>
      currentUrl.pathname === nextUrl.pathname
        ? false
        : defaultShouldRevalidate,
    children: [
      { index: true, Component: MyCourses },
      { path: 'checkout', Component: Checkout, ErrorBoundary: CheckoutError },
      { path: 'courses/:slug', Component: CourseDetail },
      { path: 'profile', Component: ProfilePage },
    ],
  },
  { path: '/signin', Component: SignIn, loader: guestOnly },
  { path: '/signup', Component: SignUp, loader: guestOnly },
  { path: '/forgot-password', Component: ForgotPassword, loader: guestOnly },
  {
    path: '/reset-password-email-sent',
    Component: ResetPasswordEmailSent,
    loader: guestOnly,
  },
  { path: '/password-reset', Component: PasswordReset, loader: guestOnly },
  {
    path: '/reset-password',
    Component: ResetPassword,
    ErrorBoundary: ResetPasswordError,
    loader: async ({ request }) => {
      const actionCodeURL = parseActionCodeURL(request.url);
      if (!actionCodeURL) {
        throw new Error('This reset link is not valid.');
      }
      if (actionCodeURL.operation !== 'PASSWORD_RESET') {
        throw new Error('This reset link is not valid.');
      }
      if (!actionCodeURL.code) {
        throw new Error('This reset link is missing its verification code.');
      }

      try {
        const email = await verifyPasswordResetCode(auth, actionCodeURL.code);
        return { email, oobCode: actionCodeURL.code };
      } catch (error) {
        console.error(error);
        throw new Error('This reset link is invalid or has expired.');
      }
    },
  },
]);

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: createMutationErrorHandler({
      i18n,
      fallbackMessage: 'Something went wrong',
      onUnauthorized: () => {
        toast.error('Please sign in again');
        window.location.href = '/signin';
      },
    }),
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      console.error(error);
    },
  }),
});

client.setOptions({
  token: () => {
    if (auth.currentUser) {
      return getIdToken(auth.currentUser);
    }
    return '';
  },
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
